import axios, { AxiosRequestConfig } from 'axios';
import { APIMarketValuation } from './market-valuation';
import { ApiUser } from './user';
import { ApiConfig } from './config';
import { ApiTask } from './task';
import { ApiTaskProductType } from './task-product-type';
import { ApiTaskProductPaymentType } from './task-product-payment-type';
import { ApiAxiosInstance, ListDataParams, ListExportParams } from './types';
import { ApiDashboard } from './dashboard';
import { ApiCalendar } from './calendar';
import { ApiNotification } from './notification';
import { ApiWorkflow } from './workflow';
import { ApiPipeline } from './pipeline';
import { ApiTeam } from './team';
import { ApiPolicy } from './policy';
import { ApiExport } from './export';
import { ApiType } from './type';
import { ApiPriority } from './priority';
import { ApiCompany } from '@api-client/company';
import { ApiCompanyContract } from '@api-client/company-contract';
import { ApiIndividual } from '@api-client/individual';
import { ApiEmployeeSize } from '@api-client/employee-size';
import { ApiIndustry } from '@api-client/industry';
import { ApiJobTitle } from '@api-client/jobTitle';
import { ApiProduct } from '@api-client/product';
import { ApiProducts } from '@api-client/products';
import { ApiMembership } from './membership';
import { ApiQuote } from '@api-client/quote';
import { ApiRevenueStream } from './revenue-stream';
import { ApiNote } from '@api-client/note';
import { ApiInvoice } from '@api-client/invoice';
import { ApiDocument } from '@api-client/document';
import { ApiDocumentCategory } from '@api-client/document-category';
import { ApiActivityLog } from '@api-client/activity-log';
import { ApiTermsAndConditions } from '@api-client/terms-and-conditions';
import { ApiTenantClient } from './client-tenant';
import { ApiGlobalSearch } from './global-search';
import { ApiTenantClientExternal } from './client-external';
import { ApiEmail } from '@api-client/email';
import { ApiBDXImporter } from './bdxImporter';
import { ApiXero } from './xero';
import { ApiBrokerData } from './broker-data';
import { ApiDocumentTemplates } from './document-templates';
import { ApiCompanies } from './companies';
import { ApiContactData } from './contact-data';
import { ApiConfidenceLevels } from './confidence-levels';
import { ApiDocsSurfaces } from './docs-surfaces';
import { ApiRolesAndPermissions } from './roles-and-permissions';
import { ApiFlightPlan } from './flightPlan';
import { ApiFlightplanKyb } from './flightplan-kyb';
import { ApiDataLists } from './data-lists';
import { ApiTypes } from './types/index';
import { ApiTeams } from './teams';
import { ApiRole } from './role';
import { ApiOffshore } from './offshore';
import { ApiTaskProducts } from './task-products';
import { ApiMarketNews } from './market-news';
import { ApiProviders } from './providers';
import { ApiSubProducts } from './sub-products';
import { ApiTwilio } from './twilio';
import { ApiCallGroups } from './call-groups';
import { ApiChat } from './chat';
import { ApiPhoneCall } from './phone-call';
import { ApiOfflineProducts } from './offline-products';
import { clearStoredLoginData } from '@utils/handleLoginResponse';
import { buildLoginPathWithRedirect } from '@utils/oauthPostLoginRedirect';
import { ApiCustomFields } from './custom-fields';
import { ApiAkoni } from './akoni';
import { ApiFeeStructures } from './fee-structures';
import { ApiFeeAllocations } from './fee-allocations';
import { ApiFeeExceptions } from './fee-exceptions';
import { ApiFeeVersions } from './fee-versions';
import { ApiAudiences } from './audiences';
import { ApiCampaigns } from './campaigns';
import { ApiAudienceManifest } from './audience-manifest';
import { ApiAudiencePresets } from './audience-presets';
import { ApiMissions } from './missions';
import { ApiSavedFilters } from './saved-filters';
import { ApiBulkImport } from './bulk-import';
import { ApiForecast } from './forecast';
import { ApiPipelineStageWorkflows } from './pipeline-stage-workflows';
import { ApiObjectives } from './objectives';
import { ApiSentimentConfiguration } from './sentiment-configuration';
import { ApiSharedMailboxes } from './shared-mailboxes';
import { ApiRelationshipTags } from './relationship-tags';
import { ApiMeetings } from './meetings';
import { ApiTeamMembers } from './team-members';
import { ApiBusinessFunctions } from './business-functions';

function isManifestEngineRequest(url?: string): boolean {
  return url?.includes('/manifest-engine') ?? false;
}

export function getXSRFToken() {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; XSRF-TOKEN=`);
  if (parts.length !== 2) return undefined;
  const raw = parts.pop()?.split(';').shift();
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export default class ApiClient {
  protected client: ApiAxiosInstance;

  // Define all api client classes here
  user: ApiUser;
  marketValuation: APIMarketValuation;
  bdxImporter: ApiBDXImporter;
  config: ApiConfig;
  dashboard: ApiDashboard;
  notification: ApiNotification;
  workflow: ApiWorkflow;
  pipeline: ApiPipeline;
  team: ApiTeam;
  teams: ApiTeams;
  policy: ApiPolicy;
  export: ApiExport;
  task: ApiTask;
  taskProductType: ApiTaskProductType;
  taskProductPaymentType: ApiTaskProductPaymentType;
  type: ApiType;
  types: ApiTypes;
  confidenceLevels: ApiConfidenceLevels;
  docsSurfaces: ApiDocsSurfaces;
  rolesAndPermissions: ApiRolesAndPermissions;
  priority: ApiPriority;
  company: ApiCompany;
  companyContract: ApiCompanyContract;
  phoneCall: ApiPhoneCall;
  companies: ApiCompanies;
  contactData: ApiContactData;
  flightPlan: ApiFlightPlan;
  flightplanKyb: ApiFlightplanKyb;
  brokerData: ApiBrokerData;
  individual: ApiIndividual;
  employeeSize: ApiEmployeeSize;
  industry: ApiIndustry;
  jobTitle: ApiJobTitle;
  product: ApiProduct;
  calendar: ApiCalendar;
  products: ApiProducts;
  membership: ApiMembership;
  quote: ApiQuote;
  revenueStream: ApiRevenueStream;
  note: ApiNote;
  invoice: ApiInvoice;
  termsAndConditions: ApiTermsAndConditions;
  tenantClient: ApiTenantClient;
  tenantClientExternal: ApiTenantClientExternal;
  activityLog: ApiActivityLog;
  document: ApiDocument;
  documentCategory: ApiDocumentCategory;
  email: ApiEmail;
  xero: ApiXero;
  globalSearch: ApiGlobalSearch;
  documentTemplates: ApiDocumentTemplates;
  dataLists: ApiDataLists;
  role: ApiRole;
  offshore: ApiOffshore;
  taskProducts: ApiTaskProducts;
  marketNews: ApiMarketNews;
  providers: ApiProviders;
  subProducts: ApiSubProducts;
  twilio: ApiTwilio;
  callGroups: ApiCallGroups;
  customFields: ApiCustomFields;
  chat: ApiChat;
  offlineProducts: ApiOfflineProducts;
  akoni: ApiAkoni;
  feeStructures: ApiFeeStructures;
  feeAllocations: ApiFeeAllocations;
  feeExceptions: ApiFeeExceptions;
  feeVersions: ApiFeeVersions;
  audiences: ApiAudiences;
  campaigns: ApiCampaigns;
  audienceManifest: ApiAudienceManifest;
  audiencePresets: ApiAudiencePresets;
  missions: ApiMissions;
  savedFilters: ApiSavedFilters;
  bulkImport: ApiBulkImport;
  forecast: ApiForecast;
  pipelineStageWorkflows: ApiPipelineStageWorkflows;
  objectives: ApiObjectives;
  meetings: ApiMeetings;
  relationshipTags: ApiRelationshipTags;
  sentimentConfiguration: ApiSentimentConfiguration;
  sharedMailboxes: ApiSharedMailboxes;
  teamMembers: ApiTeamMembers;
  businessFunctions: ApiBusinessFunctions;

  constructor() {
    this.client = this.initAxiosInstance();

    // Initialise all api client classes here
    this.user = new ApiUser(this.client);
    this.marketValuation = new APIMarketValuation(this.client);
    this.bdxImporter = new ApiBDXImporter(this.client);
    this.config = new ApiConfig(this.client);
    this.dashboard = new ApiDashboard(this.client);
    this.notification = new ApiNotification(this.client);
    this.workflow = new ApiWorkflow(this.client);
    this.pipeline = new ApiPipeline(this.client);
    this.team = new ApiTeam(this.client);
    this.teams = new ApiTeams(this.client);
    this.policy = new ApiPolicy(this.client);
    this.export = new ApiExport(this.client);
    this.task = new ApiTask(this.client);
    this.taskProductType = new ApiTaskProductType(this.client);
    this.taskProductPaymentType = new ApiTaskProductPaymentType(this.client);
    this.type = new ApiType(this.client);
    this.types = new ApiTypes(this.client);
    this.confidenceLevels = new ApiConfidenceLevels(this.client);
    this.docsSurfaces = new ApiDocsSurfaces(this.client);
    this.priority = new ApiPriority(this.client);
    this.company = new ApiCompany(this.client);
    this.companyContract = new ApiCompanyContract(this.client);
    this.companies = new ApiCompanies(this.client);
    this.contactData = new ApiContactData(this.client);
    this.brokerData = new ApiBrokerData(this.client);
    this.individual = new ApiIndividual(this.client);
    this.employeeSize = new ApiEmployeeSize(this.client);
    this.industry = new ApiIndustry(this.client);
    this.jobTitle = new ApiJobTitle(this.client);
    this.product = new ApiProduct(this.client);
    this.calendar = new ApiCalendar(this.client);
    this.products = new ApiProducts(this.client);
    this.phoneCall = new ApiPhoneCall(this.client);
    this.membership = new ApiMembership(this.client);
    this.quote = new ApiQuote(this.client);
    this.revenueStream = new ApiRevenueStream(this.client);
    this.note = new ApiNote(this.client);
    this.invoice = new ApiInvoice(this.client);
    this.termsAndConditions = new ApiTermsAndConditions(this.client);
    this.tenantClient = new ApiTenantClient(this.client);
    this.tenantClientExternal = new ApiTenantClientExternal(this.client);
    this.activityLog = new ApiActivityLog(this.client);
    this.document = new ApiDocument(this.client);
    this.documentCategory = new ApiDocumentCategory(this.client);
    this.email = new ApiEmail(this.client);
    this.xero = new ApiXero(this.client);
    this.globalSearch = new ApiGlobalSearch(this.client);
    this.rolesAndPermissions = new ApiRolesAndPermissions(this.client);
    this.documentTemplates = new ApiDocumentTemplates(this.client);
    this.flightPlan = new ApiFlightPlan(this.client);
    this.flightplanKyb = new ApiFlightplanKyb(this.client);
    this.dataLists = new ApiDataLists(this.client); // Add this line
    this.role = new ApiRole(this.client);
    this.offshore = new ApiOffshore(this.client);
    this.taskProducts = new ApiTaskProducts(this.client);
    this.marketNews = new ApiMarketNews(this.client);
    this.providers = new ApiProviders(this.client);
    this.subProducts = new ApiSubProducts(this.client);
    this.twilio = new ApiTwilio(this.client);
    this.callGroups = new ApiCallGroups(this.client);
    this.customFields = new ApiCustomFields(this.client);
    this.chat = new ApiChat(this.client);
    this.offlineProducts = new ApiOfflineProducts(this.client);
    this.akoni = new ApiAkoni(this.client);
    this.companies.setApiClient(this);
    this.individual.setApiClient(this);
    this.pipeline.setApiClient(this);
    this.task.setApiClient(this);
    this.phoneCall.setApiClient(this);
    this.feeStructures = new ApiFeeStructures(this.client);
    this.feeAllocations = new ApiFeeAllocations(this.client);
    this.feeExceptions = new ApiFeeExceptions(this.client);
    this.feeVersions = new ApiFeeVersions(this.client);
    this.audiences = new ApiAudiences(this.client);
    this.campaigns = new ApiCampaigns(this.client);
    this.audienceManifest = new ApiAudienceManifest(this.client);
    this.audiencePresets = new ApiAudiencePresets(this.client);
    this.missions = new ApiMissions(this.client);
    this.savedFilters = new ApiSavedFilters(this.client);
    this.bulkImport = new ApiBulkImport(this.client);
    this.forecast = new ApiForecast(this.client);
    this.pipelineStageWorkflows = new ApiPipelineStageWorkflows(this.client);
    this.objectives = new ApiObjectives(this.client);
    this.meetings = new ApiMeetings(this.client);
    this.relationshipTags = new ApiRelationshipTags(this.client);
    this.sentimentConfiguration = new ApiSentimentConfiguration(this.client);
    this.sharedMailboxes = new ApiSharedMailboxes(this.client);
    this.teamMembers = new ApiTeamMembers(this.client);
    this.businessFunctions = new ApiBusinessFunctions(this.client);
  }

  private initAxiosInstance() {
    // Setup axios instance with default props for our app
    this.client = axios.create({
      baseURL: import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_URL,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      withCredentials: true
    }) as ApiAxiosInstance;

    // Add request interceptor to ensure CSRF token is set
    this.client.interceptors.request.use(
      (config) => {
        // Default Content-Type is application/json. Axios then JSON-stringifies
        // FormData and files never reach PHP. Clear it so the browser sets
        // multipart/form-data with a boundary.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          config.headers.setContentType(null);
        }

        // Add auth token to all requests if available
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add the CSRF token to all requests if available
        const xsrfToken = getXSRFToken();
        if (xsrfToken) {
          config.headers['X-XSRF-TOKEN'] = xsrfToken;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Setup list method that handles pagination/filters/sorting
    this.client.getList = (
      url: string,
      listData?: ListDataParams | ListExportParams,
      config?: AxiosRequestConfig
    ) => {
      const urlParams = new URLSearchParams();
      if (listData && 'page' in listData && listData.page)
        urlParams.set('page[number]', listData.page.toString());
      if (listData && 'perPage' in listData && listData.perPage)
        urlParams.set('page[size]', listData.perPage.toString());
      if (listData?.filters) {
        Object.entries(listData.filters).forEach(([filterKey, filterValue]) => {
          urlParams.set(`filter[${filterKey}]`, `${filterValue}`);
        });
      }
      if (listData?.sort) {
        urlParams.set(
          'sort',
          (listData.sort.direction === 'desc' ? '-' : '') + listData.sort.field
        );
      }
      if (listData?.group) {
        urlParams.set('group', listData.group.toString());
      }
      // this is used specfically for the linked individuals autoselect list in the company details page as the listed individuals need to be associated to the companies tenancy
      if (
        listData &&
        'for_linking_to_company_id' in listData &&
        listData.for_linking_to_company_id
      ) {
        urlParams.set(
          'for_linking_to_company_id',
          listData.for_linking_to_company_id
        );
      }
      const fullUrl =
        url + (url.indexOf('?') !== -1 ? '&' : '?') + urlParams.toString();
      return this.client.get(fullUrl, config);
    };

    /**
     * Interceptor to handle use session timeout
     */
    this.client.interceptors.response.use(
      function (response) {
        // Only redirect if we're not already on the login page and not making an auth request
        if (
          response.request.responseURL.includes('/login') &&
          !window.location.pathname.includes('/login') &&
          !response.config.url?.includes('/api/auth') &&
          !isManifestEngineRequest(response.config.url) &&
          !window.location.pathname.includes('/user-setup')
        ) {
          window.location.assign(
            buildLoginPathWithRedirect(
              `${window.location.pathname}${window.location.search}`
            )
          );
        }
        return response;
      },
      function (error) {
        // Handle session timeout returned as 400 with specific message
        if (
          error.response?.status === 400 &&
          error.response?.data?.error === 'Session expired' &&
          !window.location.pathname.includes('/login') &&
          !error.config?.url?.includes('/api/auth') &&
          !isManifestEngineRequest(error.config?.url) &&
          !window.location.pathname.includes('/user-setup')
        ) {
          const companyUuid =
            localStorage.getItem('loggedin_tenant_company_uuid') || '';
          // Clear any stored auth/session data
          clearStoredLoginData();
          window.location.href = buildLoginPathWithRedirect(
            `${window.location.pathname}${window.location.search}`,
            {
              reason: 'inactivity',
              ...(companyUuid ? { uuid: companyUuid } : {})
            }
          );
          return Promise.reject(error);
        }
        // Only redirect on 401 if we're not already on the login page and not making an auth request
        if (
          error.response?.status === 401 &&
          !window.location.pathname.includes('/login') &&
          !error.config.url?.includes('/api/auth') &&
          !isManifestEngineRequest(error.config?.url) &&
          !window.location.pathname.includes('/user-setup')
        ) {
          window.location.href = buildLoginPathWithRedirect(
            `${window.location.pathname}${window.location.search}`
          );
        }
        return Promise.reject(error);
      }
    );

    return this.client;
  }

  // Public methods for making requests
  public async get(url: string, config?: AxiosRequestConfig) {
    // If the URL starts with /api/, remove it since it's in the baseURL
    const cleanUrl = url.startsWith('/api/') ? url.substring(4) : url;
    return this.client.get(cleanUrl, config);
  }

  public async post(url: string, data?: any, config?: AxiosRequestConfig) {
    const cleanUrl = url.startsWith('/api/') ? url.substring(4) : url;
    return this.client.post(cleanUrl, data, config);
  }

  public async put(url: string, data?: any, config?: AxiosRequestConfig) {
    const cleanUrl = url.startsWith('/api/') ? url.substring(4) : url;
    return this.client.put(cleanUrl, data, config);
  }

  public async delete(url: string, config?: AxiosRequestConfig) {
    const cleanUrl = url.startsWith('/api/') ? url.substring(4) : url;
    return this.client.delete(cleanUrl, config);
  }

  public async patch(url: string, data?: any, config?: AxiosRequestConfig) {
    const cleanUrl = url.startsWith('/api/') ? url.substring(4) : url;
    return this.client.patch(cleanUrl, data, config);
  }
}
